import {
    type CSSProperties,
    type KeyboardEvent,
    type MouseEvent,
    type ReactNode,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useMove } from "react-aria";

import "./BTBottomSheet.less";

import { BTDrawer, type IBTDrawerProps } from "../BTDrawer/BTDrawer";

export interface IBTBottomSheetProps
    extends Pick<
        IBTDrawerProps,
        "visible" | "onClose" | "destroyOnClose" | "zIndex" | "keyboard" | "role" | "aria-label"
    > {
    children: ReactNode;
    /** Optional class name for the bottom sheet */
    className?: string;
    /**
     * Makes the drag handle interactive: drag it down to dismiss the sheet, and tap or press
     * Enter/Space/ArrowDown to close it. Without this the handle is a decorative pill.
     *
     * Opt in per sheet rather than by default, so sheets whose content owns the top of the
     * gesture area aren't given a dismissal path they weren't designed around. Requires
     * `onClose`.
     * @default false
     */
    swipeToDismiss?: boolean;
}

/** Drag distance that dismisses the sheet, whichever of the two is larger. */
const MIN_CLOSE_DISTANCE_PX = 64;
const CLOSE_DISTANCE_RATIO = 0.25;
/** A short but fast flick dismisses too, so the gesture doesn't require a full-height drag. */
const FLICK_VELOCITY_PX_PER_MS = 0.5;
const MIN_FLICK_DISTANCE_PX = 24;
/** Past this gap since the last move, the finger had stopped and the velocity no longer counts. */
const VELOCITY_STALE_AFTER_MS = 100;

const baseContentWrapperStyle: CSSProperties = {
    height: "auto",
    maxHeight: "85vh",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    overflow: "hidden",
};

const drawerStyle: CSSProperties = {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    display: "flex",
    flexDirection: "column",
    maxHeight: "85vh",
};

const bodyStyle: CSSProperties = {
    padding: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    flex: 1,
    minHeight: 0,
};

/**
 * A mobile-optimized bottom sheet component that slides up from the bottom.
 * Wraps BTDrawer with iOS-style bottom sheet styling including:
 * - Rounded top corners
 * - Auto height with max 85vh
 * - Drag handle indicator, optionally draggable via `swipeToDismiss`
 */
export function BTBottomSheet({
    children,
    visible,
    onClose,
    className,
    destroyOnClose = true,
    role = "dialog",
    swipeToDismiss = false,
    // The drag handle is decorative and contributes no name, so a role="dialog" with no heading
    // of its own would announce as unlabelled. Sheets that render their own heading can leave
    // this alone; the fallback only applies when nothing else names the sheet.
    "aria-label": ariaLabel = "Bottom sheet",
    ...otherProps
}: IBTBottomSheetProps) {
    const handleRef = useRef<HTMLDivElement>(null);
    const [dragOffsetPx, setDragOffsetPx] = useState(0);
    const [isDragging, setIsDragging] = useState(false);

    // The gesture reads and writes these synchronously across move events, so they can't live in
    // state — onMoveEnd needs the final values in the same tick it decides whether to dismiss.
    const gestureRef = useRef({
        /** Clamped to >= 0: the sheet follows the finger down but never lifts above its resting position. */
        offsetPx: 0,
        /** Unclamped, so an upward keyboard arrow is distinguishable from a downward one. */
        rawOffsetPx: 0,
        velocityPxPerMs: 0,
        lastSampleTimeMs: 0,
        lastSampleOffsetPx: 0,
        sheetHeightPx: 0,
        /** Suppresses the tap-to-close click that follows a drag's pointerup. */
        didDrag: false,
    });

    /**
     * `event` is whatever DOM event triggered the dismissal, forwarded to `onClose` the way the
     * scrim and Escape paths do. A drag has no single DOM event to attribute the close to, so it
     * passes none — consumers that read the argument only ever get it from a real click or keypress.
     */
    const closeSheet = (event?: KeyboardEvent<HTMLDivElement> | MouseEvent<HTMLDivElement>) => {
        // Zero the offset before closing so this render drops our transform override and rc-drawer
        // regains control of the wrapper — otherwise translateY(offset) would beat its
        // translateY(100%) and the sheet would vanish instead of sliding out. Both updates land in
        // the same call stack, so the intermediate state is never painted.
        setDragOffsetPx(0);
        setIsDragging(false);
        gestureRef.current.offsetPx = 0;
        onClose?.(event as Parameters<NonNullable<typeof onClose>>[0]);
    };

    // A close from the scrim or Escape can land mid-drag, and a drag that dismisses the sheet
    // leaves `didDrag` set because the click that would clear it never arrives. Reset on every
    // visibility change so the next open starts at rest and its first tap isn't swallowed.
    useEffect(() => {
        setDragOffsetPx(0);
        setIsDragging(false);
        gestureRef.current.offsetPx = 0;
        gestureRef.current.didDrag = false;
    }, [visible]);

    const { moveProps } = useMove({
        onMoveStart: () => {
            const wrapper = handleRef.current?.closest(".ant-drawer-content-wrapper");
            gestureRef.current = {
                offsetPx: 0,
                rawOffsetPx: 0,
                velocityPxPerMs: 0,
                lastSampleTimeMs: performance.now(),
                lastSampleOffsetPx: 0,
                sheetHeightPx: wrapper?.getBoundingClientRect().height ?? 0,
                didDrag: true,
            };
            setIsDragging(true);
        },
        onMove: (e) => {
            const gesture = gestureRef.current;
            gesture.rawOffsetPx += e.deltaY;
            gesture.offsetPx = Math.max(0, gesture.rawOffsetPx);

            const nowMs = performance.now();
            const elapsedMs = nowMs - gesture.lastSampleTimeMs;
            if (elapsedMs > 0) {
                gesture.velocityPxPerMs =
                    (gesture.offsetPx - gesture.lastSampleOffsetPx) / elapsedMs;
                gesture.lastSampleTimeMs = nowMs;
                gesture.lastSampleOffsetPx = gesture.offsetPx;
            }

            setDragOffsetPx(gesture.offsetPx);
        },
        onMoveEnd: (e) => {
            const gesture = gestureRef.current;
            const { offsetPx, rawOffsetPx, sheetHeightPx } = gesture;

            const settleToRest = () => {
                setIsDragging(false);
                setDragOffsetPx(0);
                gesture.offsetPx = 0;
            };

            if (e.pointerType === "keyboard") {
                // Arrow keys move by a synthetic 1px, which no distance threshold would ever clear,
                // so a downward press dismisses outright. Sideways and upward presses do nothing.
                // The keyboard path produces no click, so clear didDrag here instead.
                gesture.didDrag = false;
                settleToRest();
                if (rawOffsetPx > 0) {
                    closeSheet();
                }
                return;
            }

            // Velocity is only meaningful if the finger was still moving at release. Holding
            // steady before letting go would otherwise flick the sheet closed on a stale reading.
            const isVelocityStale =
                performance.now() - gesture.lastSampleTimeMs > VELOCITY_STALE_AFTER_MS;
            const isFlick =
                !isVelocityStale &&
                offsetPx >= MIN_FLICK_DISTANCE_PX &&
                gesture.velocityPxPerMs > FLICK_VELOCITY_PX_PER_MS;

            const closeDistancePx = Math.max(
                MIN_CLOSE_DISTANCE_PX,
                sheetHeightPx * CLOSE_DISTANCE_RATIO,
            );

            if (offsetPx > closeDistancePx || isFlick) {
                closeSheet();
                return;
            }

            // Short of the threshold: drop back to rest and let the drawer's own transform
            // transition carry it, which reads as a spring-back.
            settleToRest();
        },
    });

    const contentWrapperStyle = useMemo((): CSSProperties => {
        if (dragOffsetPx <= 0 && !isDragging) {
            // Emitting no transform at rest is deliberate: contentWrapperStyle is spread over
            // rc-drawer's own inline transform, so a translateY(0) here would override the
            // translateY(100%) it applies to animate the sheet closed.
            return baseContentWrapperStyle;
        }

        return {
            ...baseContentWrapperStyle,
            transform: `translateY(${dragOffsetPx}px)`,
            // antd puts a 0.3s transform transition on every drawer child; leaving it on makes the
            // sheet lag behind the finger. Restoring it on release is what animates the snap back.
            transition: isDragging ? "none" : undefined,
            willChange: isDragging ? "transform" : undefined,
        };
    }, [dragOffsetPx, isDragging]);

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        // Arrow keys belong to useMove; only add the activation keys a role="button" implies.
        moveProps.onKeyDown?.(e);
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            closeSheet(e);
        }
    };

    const handleClick = (e: MouseEvent<HTMLDivElement>) => {
        // Every pointerup on the handle is followed by a click, including the one that ends a
        // drag. Without this guard, releasing a below-threshold drag would snap the sheet back
        // and then immediately close it anyway.
        if (gestureRef.current.didDrag) {
            gestureRef.current.didDrag = false;
            return;
        }
        closeSheet(e);
    };

    return (
        <BTDrawer
            visible={visible}
            onClose={onClose}
            placement="bottom"
            closable={false}
            className={`bt-bottom-sheet ${className ?? ""}`}
            destroyOnClose={destroyOnClose}
            contentWrapperStyle={contentWrapperStyle}
            drawerStyle={drawerStyle}
            bodyStyle={bodyStyle}
            role={role}
            aria-label={ariaLabel}
            {...otherProps}
        >
            {swipeToDismiss ? (
                <div
                    ref={handleRef}
                    className="bt-bottom-sheet-handle bt-bottom-sheet-handle-draggable"
                    role="button"
                    aria-label="Drag handle"
                    tabIndex={0}
                    data-testid="bt-bottom-sheet-handle"
                    {...moveProps}
                    onKeyDown={handleKeyDown}
                    onClick={handleClick}
                />
            ) : (
                <div
                    className="bt-bottom-sheet-handle"
                    aria-hidden="true"
                    data-testid="bt-bottom-sheet-handle"
                />
            )}
            <div className="bt-bottom-sheet-body" data-testid="bt-bottom-sheet-body">
                {children}
            </div>
        </BTDrawer>
    );
}
